import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import mainStyle from "../../../mainStyle.module.scss";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";
import { isEqual, isEmpty, cloneDeep } from "lodash";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { getAvailableAggregateGroupId } from "../../helper";
import { MenuSelect } from "components/Select/menuSelect";
import CustomMenu from "components/Menu";
import ChipTag from 'components/ChipTag';
import AggregationConfirmModal from "./AggregationConfirmModal";
import LeaveConfirmModal from "components/Drawer/Content/Settings/Profiles/Port/leaveConfirmModal";
import LinkModal from "components/LinkModal";
import useZustandStore from "zustandStore";
import { ReactComponent as ProColorIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_board_alert.svg";
import { ReactComponent as ExpandIcon } from "assets/images/icons/btn_topology_expand.svg";
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIcon } from "assets/images/icons/icn_unchecked.svg";
import { ReactComponent as CheckChangedIcon } from "assets/images/icons/icn_check_changed.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin.svg";

import useAuthScope from "hooks/useAuthScope";

// const
import { formLimitation } from "const";
import {
  defaultPortTypes,
  defaultStateTypes,
  rawSpeedToOptionValue,
  defaultAccessPolicyTypeList,
  accessPolicyTypeDefinition,
  defaultOperationList,
  operationDefinition,
} from "../../../const";

import MacWhitelistSection from "../components/macWhitelistSection";
import RadiusProfileSection from "../components/radiusProfileSection";

// Helper
import {
  getPortTagsRequestParams,
  getPortTagsCreatePayload,
  initChips,
  getPortMediaType,
  checkPortInOtherAggregateGroup,
  getUpdatedAssociatedPorts
} from "./helper";

// Utils
import {
  calcVlanIdsCountByDevice
} from 'utils/validate';

import {
  getSwitchProfileOverrideValues,
  isAccessPolicyUnsupported
} from '../../helper/main';


const legendItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  color: "text.secondary"
};

const proIconStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: 8,
  marginTop: -12,
  marginBottom: -12,
  flexShrink: 0,
};

const portBoxStyle = (status, theme) => {
  const base = {
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    boxSizing: "border-box",
  };

  if (status === "source") {
    return {
      ...base,
      backgroundColor: theme?.palette?.primary?.blueSecound || "#87A2E2",
      color: "#ffffff",
      cursor: "default",
    };
  }
  if (status === "destination" || status === "selected") {
    return {
      ...base,
      backgroundColor: theme?.palette?.primary?.main || "#172664",
      color: "#ffffff",
    };
  }
  if (status === "disabled") {
    return {
      ...base,
      backgroundColor: "#e9ecef",
      color: "#adb5bd",
      opacity: 0.6,
      cursor: "not-allowed",
    };
  }
  return {
    ...base,
    backgroundColor: "#ffffff",
    color: theme?.palette?.primary?.main || "#172664",
    border: "1px solid #DDDDDD",
    "&:hover": {
      borderColor: "#909399",
      backgroundColor: "#ebedf0",
    }
  };
};

const SwitchEditModal = ({
  setAlertInfo,
  openSwitchEditModal,
  setOpenSwitchEditModal,
  handleCloseEditModal,
  selectedRows,
  originalRowData,
  setOriginalRowData,
  switchesData,
  setSwitchesData,
  onUpdateBlockerData,
  profileSwitchList,
  linkSpeedOptions,
  initCallback,
  onEditCallback,
  scheduleOptions,
  radiusProfiles,
  macProfiles,
  onGoToPage,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const uuids = useMemo(() => {
    return {
      portTags: {
        read: uuidv4(),
        create: uuidv4(),
      },
      chipTags: {
        update: uuidv4(),
      }
    }
  }, []);

  const [searchValue, setSearchValue] = useState("");

  const [leaveModalState, setLeaveModalState] = useState({
    open: false,
    profileType: "",
    page: ""
  });

  const handleLeaveConfirm = () => {
    setLeaveModalState(prev => ({ ...prev, open: false }));
    handleCloseEditModal();
    onGoToPage?.("profiles", leaveModalState.page);
  };
  const [removeAnchorEl, setRemoveAnchorEl] = useState(null);
  const [addAnchorEl, setAddAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isEdited, setIsEdited] = useState({});
  const [showAggregationConfirmModal, setShowAggregationConfirmModal] = useState(false);

  const devicePorts = useMemo(() => {
    const macAddress = selectedRows[0]?.original?.macAddress;
    if (!macAddress) return [];
    return switchesData
      .filter((p) => p.macAddress === macAddress)
      .sort((a, b) => a.portIndex - b.portIndex);
  }, [switchesData, selectedRows]);

  const isPortInOtherAggregateGroup = (portItem) => {
    return checkPortInOtherAggregateGroup({
      portItem,
      macAddress: selectedRows[0]?.original?.macAddress,
      selectedIndexList: selectedRows.map(r => Number(r.original?.portIndex)),
      switchesData
    });
  };

  const [editedValues, setEditedValues] = useState({});
  const [originalEditValues, setOriginalEditValues] = useState({});
  const isEmptyEditedValues = useMemo(
    () => Object.keys(editedValues).length === 0,
    [editedValues]
  );
  const [selectedProfile, setSelectedProfile] = useState(null);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault, [selectedProfile?.isDefault]);

  const modalOperationList = useMemo(() => {
    return defaultOperationList;
  }, []);

  const sameModelPorts = useMemo(() => switchesData.filter(loopPort => loopPort.modelName === editedValues.modelName), [switchesData, editedValues]);
  const vlanIdsCount = useMemo(() => calcVlanIdsCountByDevice(sameModelPorts, editedValues), [sameModelPorts, editedValues]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const isAccessPolicyHidden = useMemo(() => {
    return selectedRows?.some((row) => isAccessPolicyUnsupported(row.original?.modelName ?? editedValues?.modelName));
  }, [selectedRows, editedValues?.modelName]);
  const [isChanged, setIsChanged] = useState(false);

  const [originalPortChips, setOriginalPortChips] = useState([]);
  const [portChips, setPortChips] = useState([]);
  const [portTags, setPortTags] = useState([]);

  const { isCopper, isFiber, isCombo } = useMemo(() => getPortMediaType({ selectedRows }), [selectedRows]);

  const theme = useTheme();
  const deviceNames =
    selectedRows?.map?.((row) => row.original.deviceName) || [];

  const extraCount = deviceNames.length > 1 ? deviceNames.length - 1 : 0;

  const deviceMac = selectedRows?.[0]?.original?.macAddress;
  const isPortSupportPoE = (p) => {
    if (isEmpty(p?.poePorts) || p?.portIndex == null) return false;
    return p.poePorts[p.portIndex - 1] !== 0;
  };
  const isPoeSectionVisible = (selectedRows || []).some(r => isPortSupportPoE(r.original));
  const hasPortProfile = !!selectedProfile && !selectedProfile.isDefault;
  const profilePortScheduleOn = hasPortProfile && !!(selectedProfile.isPortScheduleMode ?? (selectedProfile.portState && selectedProfile.portSchedule));
  const profilePoeScheduleOn = hasPortProfile && !!(selectedProfile.isPoeScheduleMode ?? (selectedProfile.poeState && selectedProfile.poeSchedule));
  const wiredUsedSchedulesSet = useMemo(() => {
    const set = new Set();
    if (!deviceMac) return set;

    const selectedIndices = new Set((selectedRows || []).map(r => Number(r.original?.portIndex)));
    // Aggregate members are synchronized with the selected ports on save, so they count the live value too
    if (editedValues?.operation?.type === "aggregation") {
      (editedValues.operation.aggregatePort || []).forEach(idx => selectedIndices.add(Number(idx)));
    }
    const profileSource = hasPortProfile ? {
      isPortScheduleMode: profilePortScheduleOn,
      portSchedule: selectedProfile.portSchedule,
      isPoeScheduleMode: profilePoeScheduleOn,
      poeSchedule: selectedProfile.poeSchedule,
    } : null;

    switchesData.forEach(p => {
      if (p.macAddress === deviceMac) {
        // Selected ports use the live editedValues (or the chosen Port Profile) so UI changes are reflected immediately
        const source = selectedIndices.has(Number(p.portIndex)) ? (profileSource || editedValues) : p;
        if (source?.isPortScheduleMode && source?.portSchedule) set.add(source.portSchedule);
        // Only ports that support PoE count the PoE schedule
        const isPoeSupported = isPortSupportPoE(p);
        if (isPoeSupported && source?.isPoeScheduleMode && source?.poeSchedule) set.add(source.poeSchedule);
      }
    });

    return set;
  }, [switchesData, deviceMac, selectedRows, editedValues, selectedProfile]);

  const wiredUsedCount = wiredUsedSchedulesSet.size;
  const maxWiredSchedules = 8;
  const scheduleOptionsWithLimit = useMemo(() => {
    if (!scheduleOptions) return scheduleOptions;
    return scheduleOptions.map(opt => {
      const val = opt?.value ?? opt;
      const disabled = wiredUsedCount >= maxWiredSchedules && !wiredUsedSchedulesSet.has(val);
      return { ...opt, disabled };
    });
  }, [scheduleOptions, wiredUsedCount, wiredUsedSchedulesSet]);

  const isWiredScheduleOverLimit = wiredUsedCount > maxWiredSchedules;

  const renderScheduleUsageHint = () => (
    <>
      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: theme.palette.primary.greyPrimary }}>
          {`Wired schedules in use: `}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {`${wiredUsedCount} / ${maxWiredSchedules}`}
        </Typography>
        <Tooltip
          title="Each device supports a maximum of 8 Profiles."
          placement="right"
          arrow
          slotProps={{ tooltip: { sx: { maxWidth: "none", whiteSpace: "nowrap" } } }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", width: 16, height: 16 }}>
            <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
          </Box>
        </Tooltip>
      </Box>
      {isWiredScheduleOverLimit && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: theme.palette.primary.redPrimary }}>
          No free slot on this switch for this profile.
        </Typography>
      )}
    </>
  );

  const getLinkSpeedMixed = (rows) => {
    let intersection = null;
    const union = { fiber: new Set(), copper: new Set() };

    const parseSinglePort = (linkSpeed, portIndex) => {
      if (!Array.isArray(linkSpeed)) return { fiber: [], copper: [] };
      let currentStart = 1;

      for (const item of linkSpeed) {
        const { count, s: fiberSpeeds = [], t: copperSpeeds = [] } = item;
        const rangeStart = currentStart;
        const rangeEnd = currentStart + count - 1;

        if (portIndex >= rangeStart && portIndex <= rangeEnd) {
          return {
            fiber: Array.isArray(fiberSpeeds) ? fiberSpeeds : [],
            copper: Array.isArray(copperSpeeds) ? copperSpeeds : [],
          };
        }
        currentStart += count;
      }

      return { fiber: [], copper: [] };
    };

    rows.forEach((row) => {
      const linkSpeed = row?.original?.linkSpeed;
      const portIndex = row?.original?.portIndex;
      const { fiber, copper } = parseSinglePort(linkSpeed, portIndex);

      const currentSet = { fiber: new Set(fiber), copper: new Set(copper) };
      fiber.forEach((v) => union.fiber.add(v));
      copper.forEach((v) => union.copper.add(v));

      if (intersection === null) {
        intersection = { fiber: currentSet.fiber, copper: currentSet.copper };
      } else {
        intersection.fiber = new Set(
          [...intersection.fiber].filter((v) => currentSet.fiber.has(v))
        );
        intersection.copper = new Set(
          [...intersection.copper].filter((v) => currentSet.copper.has(v))
        );
      }
    });

    const convertToOptions = (set) =>
      [...set]
        .map((raw) => {
          const value = rawSpeedToOptionValue[raw];
          return linkSpeedOptions.find((o) => o.value === value);
        })
        .filter(Boolean)
        .sort((a, b) => a.id - b.id);

    const fiberHasUnion = union.fiber.size > 0;
    const copperHasUnion = union.copper.size > 0;

    if (fiberHasUnion && copperHasUnion) {
      return {
        fiber: convertToOptions(union.fiber),
        copper: convertToOptions(union.copper),
      };
    }

    return {
      fiber: convertToOptions(intersection.fiber),
      copper: convertToOptions(intersection.copper),
    };
  };
  const linkSpeedData = useMemo(() => {
    if (!selectedRows?.length) return { fiber: [], copper: [] };
    return getLinkSpeedMixed(selectedRows);
  }, [selectedRows]);

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    ml: "50px",
    mt: "20px",
  };

  const getInputStyles = (field) => ({
    width: "300px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.borderGreyPrimary}`,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.mainHover}`,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.main}`,
      },
    },
  });

  const handleRemoveClick = (event, tag) => {
    setRemoveAnchorEl(event.currentTarget);
    setSelectedTag(tag);
  };

  const handleRemoveClose = () => {
    setRemoveAnchorEl(null);
    setSelectedTag(null);
  };

  const handleRemove = (tagToRemove) => {
    setEditedValues((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter(
        (t) =>
          (typeof t === "string" ? t : t.value) !==
          (typeof tagToRemove === "string" ? tagToRemove : tagToRemove.value)
      ),
    }));

    setIsEdited((prev) => ({ ...prev, tags: true }));
    handleRemoveClose();
  };

  const handleAddClick = (event) => setAddAnchorEl(event.currentTarget);

  const handleAddClose = () => {
    setAddAnchorEl(null);
    setSearchValue("");
  };

  const handleAdd = (newTag) => {
    if (!newTag) return;

    setEditedValues((prev) => {
      const normalizedTags = (prev.tags || []).map((t) =>
        typeof t === "string" ? { value: t, isNew: false } : t
      );

      return {
        ...prev,
        tags: [...normalizedTags, { value: newTag, isNew: true }],
      };
    });

    setIsEdited((prev) => ({ ...prev, tags: true }));
    handleAddClose();
  };

  const handleSaveClick = () => {
    if (!isUnityProSubscribed && !isAccessPolicyHidden) {
      const isAccessPort = isProfileMode
        ? selectedProfile?.portType === "access"
        : editedValues.portType === "access";
      const isAccessPolicyEnabled = isProfileMode
        ? selectedProfile?.accessPolicy?.enableState === true
        : editedValues.accessPolicy?.enableState === true;

      if (isAccessPort && isAccessPolicyEnabled) {
        setIsLinkModalOpen(true);
        return;
      }
    }

    const opType = isProfileMode ? selectedProfile?.operation?.type : editedValues.operation?.type;
    if (!isProfileMode && opType === "aggregation") {
      setShowAggregationConfirmModal(true);
    } else {
      onSave();
    }
  };

  const onSave = () => {
    const updatedPortsMap = getUpdatedPortsMap();

    let tempSwitchesData = [...switchesData];

    const sourceIndices = selectedRows.map(row => Number(row.original.portIndex));
    selectedRows.forEach(row => {
      const original = row.original;
      const updatedPort = updatedPortsMap[original.id];
      if (updatedPort) {
        tempSwitchesData = getUpdatedAssociatedPorts({
          switchesData: tempSwitchesData,
          activeRow: original,
          originalRowData: original,
          updatedPort,
          excludeIndices: sourceIndices
        });
      }
    });

    setSwitchesData(cloneDeep(tempSwitchesData));
    onUpdateBlockerData(tempSwitchesData);

    const updatedOriginalPorts = getUpdatedOriginalPorts();
    setOriginalRowData(updatedOriginalPorts);

    setIsChanged(false);
    setOpenSwitchEditModal(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

    onEditCallback(editedValues, tempSwitchesData);
  };

  const parseRowLinkSpeed = (linkSpeed, portIndex) => {
    if (!Array.isArray(linkSpeed)) return { hasFiber: false, hasCopper: false };
    let currentStart = 1;

    for (const item of linkSpeed) {
      const { count, s: fiberSpeeds = [], t: copperSpeeds = [] } = item;
      const rangeStart = currentStart;
      const rangeEnd = currentStart + count - 1;

      if (portIndex >= rangeStart && portIndex <= rangeEnd) {
        return {
          hasFiber: Array.isArray(fiberSpeeds) && fiberSpeeds.length > 0,
          hasCopper: Array.isArray(copperSpeeds) && copperSpeeds.length > 0,
        };
      }

      currentStart += count;
    }

    return { hasFiber: false, hasCopper: false };
  };

  const getCalcLinkSpeed = ({ original }) => {
    const linkSpeed = {
      speed: null,
      comboSfpSpeed: null,
    };

    const { hasFiber, hasCopper } = parseRowLinkSpeed(
      original.linkSpeed,
      original.portIndex
    );

    const isCombo = hasFiber && hasCopper;

    if (isCombo) {
      linkSpeed.speed = editedValues.speed ?? original.speed;
      linkSpeed.comboSfpSpeed = editedValues.comboSfpSpeed ?? original.comboSfpSpeed;
    } else if (hasCopper) {
      linkSpeed.speed = editedValues.speed ?? original.speed;
      delete linkSpeed.comboSfpSpeed;
    } else if (hasFiber) {
      linkSpeed.speed = editedValues.speed ?? editedValues.comboSfpSpeed ?? original.speed;
      delete linkSpeed.comboSfpSpeed;
    }

    return linkSpeed;
  }

  const getUpdatedPortsMap = () => {
    const sourceIndices = selectedRows.map(row => Number(row.original.portIndex));

    const updatedPortsMap = Object.fromEntries(selectedRows.map((row, idx) => {
      const original = row.original;
      const calcLinkSpeed = getCalcLinkSpeed({ original });
      const { hasFiber, hasCopper } = parseRowLinkSpeed(
        original.linkSpeed,
        original.portIndex
      );
      const combinedEditedValues = {
        ...original,
        ...editedValues,
      }

      const finalPortSchedule = combinedEditedValues.isPortScheduleMode ? combinedEditedValues.portSchedule : null;
      const finalPoeSchedule = combinedEditedValues.isPoeScheduleMode ? combinedEditedValues.poeSchedule : null;

      const finalAccessPolicy = combinedEditedValues.accessPolicy ? {
        enableState: combinedEditedValues.accessPolicy.enableState,
        type: combinedEditedValues.accessPolicy.enableState ? combinedEditedValues.accessPolicy.type : "STATIC",
        macList: (combinedEditedValues.accessPolicy.enableState &&
          (combinedEditedValues.accessPolicy.type === "STATIC" || combinedEditedValues.accessPolicy.type === "DELETE_ON_TIME"))
          ? combinedEditedValues.accessPolicy.macList : null,
        radiusProfile: (combinedEditedValues.accessPolicy.enableState &&
          combinedEditedValues.accessPolicy.type === "8021X_PROFILE")
          ? combinedEditedValues.accessPolicy.radiusProfile : null,
        dynamicSize: (combinedEditedValues.accessPolicy.enableState &&
          combinedEditedValues.accessPolicy.type === "DELETE_ON_TIME")
          ? combinedEditedValues.accessPolicy.dynamicSize : null,
      } : null;

      const destinationPorts = (combinedEditedValues.operation?.aggregatePort || []).map(Number);
      const finalAggregatePorts = combinedEditedValues.operation?.type === "aggregation"
        ? [...new Set([...sourceIndices, ...destinationPorts])].filter(idx => idx !== Number(original.portIndex))
        : [];

      const isMirrorType = combinedEditedValues.operation?.type === "mirroring";
      const isFirst = idx === 0;
      const finalOpType = isMirrorType
        ? (isFirst ? "mirroring" : "switching")
        : (combinedEditedValues.operation?.type || "switching");

      const updatedPort = {
        ...combinedEditedValues,
        id: original.id,
        portIndex: original.portIndex,
        macAddress: original.macAddress,
        deviceName: original.deviceName,
        portName: combinedEditedValues.portName,
        portTags: portChips.map(chip => chip.tagId),
        portProfile: selectedProfile?.value ?? combinedEditedValues.portProfile,
        ...(calcLinkSpeed.speed !== undefined && { speed: calcLinkSpeed.speed }),
        ...(calcLinkSpeed.comboSfpSpeed !== undefined && { comboSfpSpeed: calcLinkSpeed.comboSfpSpeed }),
        ...(hasCopper && { speedT: calcLinkSpeed.speed }),
        ...(hasFiber && {
          speedS: hasCopper ? calcLinkSpeed.comboSfpSpeed : calcLinkSpeed.speed,
        }),
        portSchedule: finalPortSchedule,
        poeSchedule: finalPoeSchedule,
        accessPolicy: finalAccessPolicy,
        operation: {
          type: finalOpType,
          mirrorPort: (isMirrorType && isFirst) ? (combinedEditedValues.operation?.mirrorPort ?? []) : [],
          mirrorMode: (isMirrorType && isFirst) ? (combinedEditedValues.operation?.mirrorMode ?? "") : "",
          aggregatePort: (!isMirrorType && finalOpType === "aggregation") ? finalAggregatePorts : [],
          aggregateType: (!isMirrorType && finalOpType === "aggregation") ? (combinedEditedValues.operation?.aggregateType ?? "") : "",
          aggregateGroupId: (!isMirrorType && finalOpType === "aggregation") ? (combinedEditedValues.operation?.aggregateGroupId ?? null) : null,
        },
      };

      if (calcLinkSpeed.comboSfpSpeed === undefined) {
        delete updatedPort.comboSfpSpeed;
      }
      if (!hasFiber) delete updatedPort.speedS;
      if (!hasCopper) delete updatedPort.speedT;

      return [updatedPort.id, updatedPort];
    }));

    return updatedPortsMap;
  };

  const getUpdatedOriginalPorts = () => {
    const updatedOriginalPorts = selectedRows.map((r) => {
      const { hasFiber, hasCopper } = parseRowLinkSpeed(
        r.original.linkSpeed,
        r.original.portIndex
      );

      const updated = { ...r.original, ...editedValues };
      const isCombo = hasFiber && hasCopper;

      if (isCombo) {
        updated.speed = editedValues.speed ?? r.original.speed;
        updated.comboSfpSpeed = editedValues.comboSfpSpeed ?? r.original.comboSfpSpeed;
        updated.speedT = updated.speed;
        updated.speedS = updated.comboSfpSpeed;
      } else if (hasCopper) {
        updated.speed = editedValues.speed ?? r.original.speed;
        updated.speedT = updated.speed;
        delete updated.comboSfpSpeed;
        delete updated.speedS;
      } else if (hasFiber) {
        updated.speed = editedValues.speed ?? editedValues.comboSfpSpeed ?? r.original.speed;
        updated.speedS = updated.speed;
        delete updated.comboSfpSpeed;
        delete updated.speedT;
      }

      const finalPortSchedule = updated.isPortScheduleMode ? updated.portSchedule : null;
      const finalPoeSchedule = updated.isPoeScheduleMode ? updated.poeSchedule : null;

      const finalAccessPolicy = updated.accessPolicy ? {
        enableState: updated.accessPolicy.enableState,
        type: updated.accessPolicy.enableState ? updated.accessPolicy.type : "STATIC",
        macList: (updated.accessPolicy.enableState &&
          (updated.accessPolicy.type === "STATIC" || updated.accessPolicy.type === "DELETE_ON_TIME"))
          ? updated.accessPolicy.macList : null,
        radiusProfile: (updated.accessPolicy.enableState &&
          updated.accessPolicy.type === "8021X_PROFILE")
          ? updated.accessPolicy.radiusProfile : null,
        dynamicSize: (updated.accessPolicy.enableState &&
          updated.accessPolicy.type === "DELETE_ON_TIME")
          ? updated.accessPolicy.dynamicSize : null,
      } : null;

      updated.portSchedule = finalPortSchedule;
      updated.poeSchedule = finalPoeSchedule;
      updated.accessPolicy = finalAccessPolicy;

      return updated;
    })

    return updatedOriginalPorts;
  }

  const handleCancel = () => {
    if (selectedRows.length > 0) {
      setEditedValues(selectedRows[0].original);
      setOriginalEditValues(selectedRows[0].original);
      setPortChips(originalPortChips);

      const matchedProfile = profileSwitchList.find(profile => profile.value === selectedRows[0].original.portProfile) || null;
      setSelectedProfile(matchedProfile);
    } else {
      setEditedValues({});
      setOriginalEditValues({});
      setSelectedProfile(null);
    }

    setIsEdited({});
    setIsChanged(false);
  };

  const updateEditedValues = (field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [field]: value
    }));

    if (!isEqual(originalEditValues[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
      setIsChanged(true);
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
      setIsChanged(false);
    }
  };

  const checkAllowVlans = (value) => {
    if (!value?.trim()) return false;

    const parts = value.split(",").map((s) => s.trim());
    const used = new Set();
    const { min, max } = formLimitation.vlanId;

    for (let part of parts) {
      if (part === "") return false;

      if (part.includes("-")) {
        const [startStr, endStr] = part.split("-").map((s) => s.trim());
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);

        if (
          isNaN(start) ||
          isNaN(end) ||
          start < min ||
          end > max ||
          start > end
        )
          return false;

        for (let i = start; i <= end; i++) {
          if (used.has(i)) return false;
          used.add(i);
        }
      } else {
        const num = parseInt(part, 10);
        if (isNaN(num) || num < min || num > max) return false;

        if (used.has(num)) return false;
        used.add(num);
      }
    }

    return true;
  };

  const isFormInvalid = () => {
    let isInvalid = false;
    const { portName, portType, PVID, allowVlan, isPortScheduleMode, portSchedule, isPoeScheduleMode, poeSchedule, accessPolicy, operation } = editedValues;

    if (portName === "") isInvalid = true;
    if (vlanIdsCount.number > 128) isInvalid = true;
    if (!PVID || +PVID < 1 || +PVID > 4094) isInvalid = true;
    if (
      portType !== defaultPortTypes.access.value &&
      !checkAllowVlans(allowVlan)
    )
      isInvalid = true;

    if (isPortScheduleMode && !portSchedule) isInvalid = true;

    if (isPoeScheduleMode && !poeSchedule) isInvalid = true;

    if (isWiredScheduleOverLimit) isInvalid = true;

    if (operation?.type === "mirroring") {
      if (!operation.mirrorPort || operation.mirrorPort.length === 0) {
        isInvalid = true;
      }

      // 限制一台 device 只能有一組 mirror group.
      const sourceIndices = selectedRows.map(r => Number(r.original?.portIndex));
      const activeMac = selectedRows[0]?.original?.macAddress;

      const otherMirrorPorts = switchesData
        .filter(p => p.macAddress === activeMac && !sourceIndices.includes(Number(p.portIndex)) && p.operation?.type === "mirroring")
        .map(p => Number(p.portIndex));

      if (otherMirrorPorts.length > 0) {
        const wasAnyOriginalMirroring = selectedRows.some(row => row.original?.operation?.type === "mirroring");
        if (!wasAnyOriginalMirroring) {
          const newMirrorPorts = (operation.mirrorPort || []).map(Number);
          const hasIntersection = newMirrorPorts.some(port => otherMirrorPorts.includes(port));
          if (!hasIntersection) {
            isInvalid = true;
          }
        }
      }
    }

    if (operation?.type === "aggregation") {
      if (selectedRows.length === 1) {
        if (!operation.aggregatePort || operation.aggregatePort.length === 0) {
          isInvalid = true;
        }
      }

      // aggregateGroupId 為 null 代表這台裝置已經沒有可用的 Aggregation 群組了
      // (getAvailableAggregateGroupId 在 capabilities.aggregation 額度用完時會回傳 null).
      if (operation.aggregateGroupId == null) {
        isInvalid = true;
      }

      // capabilities.aggregation 同時也是「每組最多幾個 port」的上限,
      // 群組成員數 = 選取的來源 port 數 + aggregatePort 裡的成員數.
      const maxAggregationPorts = selectedRows[0]?.original?.aggregation;
      const currentGroupSize = selectedRows.length + (operation.aggregatePort?.length || 0);
      if (maxAggregationPorts != null && currentGroupSize > maxAggregationPorts) {
        isInvalid = true;
      }
    }

    if (!isAccessPolicyHidden && portType === "access" && accessPolicy?.enableState) {
      const { type, macList, radiusProfile, dynamicSize } = accessPolicy;
      if (type === "STATIC") {
        if (!macList) isInvalid = true;
      } else if (type === "DELETE_ON_TIME") {
        if (!macList) isInvalid = true;
        if (dynamicSize === "" || dynamicSize == null) isInvalid = true;
        const sizeNum = Number(dynamicSize);
        if (isNaN(sizeNum) || sizeNum < 1 || sizeNum > 128) isInvalid = true;
      } else if (type === "8021X_PROFILE") {
        if (!radiusProfile) isInvalid = true;
      }
    }

    return isInvalid;
  };

  const fetchPortTags = () => {
    if (isAuthReady) {
      const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "read" });

      sendRequest(
        apiPaths,
        uuids.portTags.read,
        restfulParams
      );
    }
  }

  const createPortTags = newTag => {
    const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "create" });
    const payload = getPortTagsCreatePayload({ tags: newTag });

    sendRequest(
      apiPaths,
      uuids.portTags.create,
      restfulParams,
      payload
    );
  }

  const updateChips = newChip => {
    const updateChips = [...portChips, newChip];
    setPortChips(updateChips);
  }

  const removeChips = newChip => {
    const removedChips = portChips.filter(chip => chip.tagId !== newChip.tagId);
    setPortChips(removedChips);
  }

  useEffect(() => {
    if (!dataMap?.[uuids.portTags.read]) return;

    const portTagsResp = getData(uuids.portTags.read);
    const portTagsRespData = portTagsResp?.data ?? null;

    // Read tags
    if (portTagsRespData) {
      if (portTagsRespData.hasOwnProperty("code") && portTagsRespData.code !== 200) {
        // error handling
      } else {

        const chips = initChips({ chips: editedValues?.portTags, tags: portTagsRespData })
        setPortChips(chips);
        setOriginalPortChips(chips);
        setPortTags(portTagsRespData);
      }
    }

  }, [dataMap?.[uuids.portTags.read]]);

  useEffect(() => {
    // Create tags
    if (!dataMap?.[uuids.portTags.create]) return;

    const creatingResponse = getData(uuids.portTags.create);
    if (creatingResponse) {
      fetchPortTags();
    }
  }, [dataMap?.[uuids.portTags.create]]);

  useEffect(() => {
    // Update chips
    if (!dataMap?.[uuids.chipTags.update]) return;

    const updatingResponse = getData(uuids.chipTags.update);
    if (updatingResponse) {
      initCallback();
    }
  }, [dataMap?.[uuids.chipTags.update]]);

  useEffect(() => {
    if (openSwitchEditModal && selectedRows.length > 0) {
      const firstRow = selectedRows[0].original;
      let initialValues = { ...firstRow };
      if (firstRow?.operation?.type === "aggregation") {
        const groupId = getAvailableAggregateGroupId(switchesData, firstRow.macAddress, firstRow.operation?.aggregateGroupId, firstRow.aggregation);
        initialValues = {
          ...firstRow,
          operation: {
            ...firstRow.operation,
            aggregateGroupId: groupId,
            aggregateType: (firstRow.operation?.aggregateType || "lacp").toLowerCase()
          }
        };
      }
      setEditedValues(initialValues);
      setOriginalEditValues(initialValues);
      setIsEdited({});
      setIsChanged(false);
      fetchPortTags();

      const matchedProfile = profileSwitchList.find(profile => profile.value === selectedRows[0].original.portProfile) || null;
      setSelectedProfile(matchedProfile);
    }
  }, [openSwitchEditModal, selectedRows]);

  if (isEmptyEditedValues) return <></>;

  return (
    <>
      <ModalContainer display={openSwitchEditModal} modalWidth="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Selected Ports
          </Typography>
          <Button
            onClick={handleCloseEditModal}
            sx={{
              marginRight: "-10px",
              marginTop: "-28px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              backgroundColor: theme.palette.primary.subBlue,
              borderRadius: theme.shape.customBorderRadius[0],
              gap: "10px",
              padding: "4px 8px",
            }}
          >
            {editedValues ? editedValues.portName : "No device selected"}
          </Typography>

          {extraCount > 0 && (
            <Tooltip
              title={
                <Box
                  className={mainStyle["custom-scrollbar"]}
                  sx={{
                    maxHeight: 250,
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {deviceNames.map((name, idx) => {
                    const row = selectedRows[idx]?.original;
                    return (
                      <Typography
                        sx={{
                          color: theme.palette.primary.white,
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.customFontSize[1],
                          fontWeight: theme.typography.customFontWeight[3],
                          lineHeight: 1.92,
                        }}
                        key={idx}
                      >
                        {row.portName}
                      </Typography>
                    );
                  })}
                </Box>
              }
              arrow
            >
              <Typography
                sx={{
                  ml: 3,
                  color: theme.palette.primary.main,
                  fontSize: theme.typography.customFontSize[10],
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                +{extraCount} Selected
              </Typography>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <AlertIcon />
          <Box
            sx={{
              ml: "10px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Only changes highlighted in{" "}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.orange,
                fontWeight: "bold",
              }}
            >
              Orange
            </Box>{" "}
            will be applied to all selected ports.
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {editedValues && (
            <>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  value={editedValues.portName}
                  onChange={(e) =>
                    updateEditedValues("portName", e.target.value)
                  }
                  sx={getInputStyles("portName")}
                />
              </Box>
              {/* <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Tag
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {editedValues.tags && editedValues.tags.length > 0 ? (
                    editedValues.tags.map((tag, idx) => {
                      const tagValue =
                        typeof tag === "string" ? tag : tag.value;
                      const isNew = typeof tag === "object" && tag.isNew;

                      return (
                        <span
                          key={idx}
                          style={{
                            minWidth: "46px",
                            height: "25px",
                            padding: "0 8px",
                            backgroundColor: isNew
                              ? theme.palette.primary.yellowPrimary
                              : theme.palette.primary.subBlue,
                            color: theme.palette.primary.main,
                            fontSize: theme.typography.customFontSize[9],
                            borderRadius: theme.shape.customBorderRadius[0],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleRemoveClick(e, tag)}
                        >
                          {tagValue}
                        </span>
                      );
                    })
                  ) : (
                    <span>-</span>
                  )}
                </Box>

                <CustomMenu
                  anchorEl={removeAnchorEl}
                  open={Boolean(removeAnchorEl)}
                  handleMenuClose={handleRemoveClose}
                  maxWidth={112}
                  height={41}
                  mt={2}
                  ml={8}
                  top="-12px"
                  right="78px"
                  borderLeftWidth="8px"
                  borderRightWidth="8px"
                  borderBottomWidth="12px"
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: theme.palette.primary.main,
                    }}
                    onClick={() => handleRemove(selectedTag)}
                  >
                    <RemoveIcon />
                    Remove
                  </Box>
                </CustomMenu>

                <Box sx={{ ml: 2, cursor: "pointer" }} onClick={handleAddClick}>
                  <ExpandIcon />
                </Box>

                <CustomMenu
                  anchorEl={addAnchorEl}
                  handleMenuClose={handleAddClose}
                  maxWidth={216}
                  height={268}
                  mt={2}
                  ml={22}
                  top="-12px"
                  right="174px"
                  borderLeftWidth="8px"
                  borderRightWidth="8px"
                  borderBottomWidth="12px"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                      borderBottom: `1px solid ${theme.palette.primary.mainHover}`,
                    }}
                  >
                    <TextField
                      variant="standard"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: "14px" },
                      }}
                    />
                    <Box
                      onClick={() => handleAdd(searchValue)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ExpandIcon />
                    </Box>
                  </Box>
                </CustomMenu>
              </Box> */}

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Port Profile
                </Typography>
                <MenuSelect
                  value={selectedProfile?.value || ""}
                  onChange={(opt) => {
                    const profile = profileSwitchList.find((item) => item.value === opt.value) || null;

                    setSelectedProfile(profile);

                    const overrides = getSwitchProfileOverrideValues(profile);

                    // Back to "No Profile": restore the original values (same as Revert), keep only the profile selection
                    if (profile?.isDefault) {
                      const isProfileChanged = !isEqual(originalEditValues.portProfile, overrides.portProfile);
                      setEditedValues({ ...originalEditValues, ...overrides });
                      setIsEdited(isProfileChanged ? { portProfile: true } : {});
                      setIsChanged(isProfileChanged);
                      return;
                    }

                    Object.entries(overrides).forEach(([key, value]) => {
                      if (key === "sfpSpeed") {
                        updateEditedValues("comboSfpSpeed", value);
                      } else {
                        updateEditedValues(key, value);
                      }
                    });

                    if (profile && !profile.isDefault) {
                      updateEditedValues("isPortScheduleMode", !!(profile.portState && profile.portSchedule));
                      updateEditedValues("isPoeScheduleMode", !!(profile.poeState && profile.poeSchedule));
                    }
                  }}
                  options={profileSwitchList}
                  width="300px"
                  isEditedField={isEdited.portProfile}
                />
              </Box>

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Port Tags
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  {(!Array.isArray(portChips) || portChips.length === 0) && <Box sx={{ marginRight: 1 }}>-</Box>}
                  <ChipTag
                    isChipsEdit={false}
                    chips={portChips}
                    tags={portTags}
                    onChipsRemoveChange={props => {
                      const { removedChip } = props;
                      removeChips(removedChip)
                    }}
                    onChipsAddChange={props => {
                      const { newChip } = props;
                      updateChips(newChip);
                    }}
                    onTagsChange={props => {
                      const { newTag } = props;
                      createPortTags(newTag)
                    }}
                  />
                </Box>
              </Box>

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Port State
                </Typography>
                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={profilePortScheduleOn ? "schedule" : (selectedProfile?.portState ? "enable" : "disable")}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const checked = value === "schedule" ? profilePortScheduleOn : (value === "enable" ? (selectedProfile?.portState && !profilePortScheduleOn) : (!selectedProfile?.portState && !profilePortScheduleOn));

                      return (
                        <FormControlLabel
                          disabled
                          key={"profilePortStateRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={<CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.isPortScheduleMode ? "schedule" : (editedValues.portState ? "enable" : "disable")}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateEditedValues("portState", val === "enable" || val === "schedule");
                      updateEditedValues("isPortScheduleMode", val === "schedule");
                    }}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const changed = isEdited.portState != null || isEdited.isPortScheduleMode != null;
                      const checked = value === "schedule" ? editedValues.isPortScheduleMode : (value === "enable" ? (editedValues.portState && !editedValues.isPortScheduleMode) : (!editedValues.portState && !editedValues.isPortScheduleMode));

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={"portStateRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              {isProfileMode && profilePortScheduleOn && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}></Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <MenuSelect
                      disabled
                      value={selectedProfile?.portSchedule || ""}
                      options={scheduleOptions}
                      width="300px"
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </Box>
              )}
              {!isProfileMode && editedValues.isPortScheduleMode && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}></Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <MenuSelect
                      disabled={!isEditable}
                      value={editedValues.portSchedule || ""}
                      onChange={(opt) => {
                        if (opt.value === "Create New Schedule") {
                          setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                          return;
                        }
                        updateEditedValues("portSchedule", opt.value);
                      }}
                      options={scheduleOptionsWithLimit}
                      placeholder="Select a Schedule Profile"
                      width="300px"
                      isEditedField={isEdited.portSchedule}
                      pinningOptions={[
                        {
                          value: "Create New Schedule",
                          label: "Create New Schedule",
                          showArrow: true,
                        }
                      ]}
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </Box>
              )}

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Port Type
                </Typography>
                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.portType}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultPortTypes).map((key) => {
                      const portType = defaultPortTypes[key];
                      const changed = isEdited.portType != null;

                      return (
                        <FormControlLabel
                          disabled
                          key={portType.key}
                          value={portType.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                            />
                          }
                          label={portType.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.portType}
                    onChange={(e) =>
                      updateEditedValues("portType", e.target.value)
                    }
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultPortTypes).map((key) => {
                      const portType = defaultPortTypes[key];
                      const changed = isEdited.portType != null;

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={portType.key}
                          value={portType.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                            />
                          }
                          label={portType.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  PVID
                </Typography>
                {
                  isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled
                      type="number"
                      value={selectedProfile?.PVID ?? ""}
                      sx={getInputStyles("PVID")}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      (1~4094)
                    </Typography>
                  </Box>
                }

                {
                  !isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable}
                      type="number"
                      value={editedValues.PVID ?? ""}
                      onChange={(e) => updateEditedValues("PVID", e.target.value)}
                      onBlur={(e) => {
                        const num = Number(e.target.value);
                        if (!isNaN(num)) updateEditedValues("PVID", num);
                      }}
                      sx={getInputStyles("PVID")}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      (1~4094)
                    </Typography>
                  </Box>
                }
              </Box>

              {
                vlanIdsCount.number > 128 &&
                <>
                  <Box
                    sx={{
                      ...rowStyle,
                      marginTop: "0"
                    }}
                  >
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px", minWidth: "175px !important;" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </Box>
                </>
              }

              {
                selectedProfile?.portType === defaultPortTypes.trunk.value &&
                isProfileMode &&
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                    Allow VLANs
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled
                      type="text"
                      value={selectedProfile?.allowVlan}
                      sx={getInputStyles("allowVlan")}
                      placeholder="e.g. 1, 4-7"
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      {/* (1~4094) */}
                      {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    </Typography>
                  </Box>

                </Box>
              }
              {
                editedValues.portType === defaultPortTypes.trunk.value &&
                !isProfileMode &&
                <>
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                      Allow VLANs
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        disabled={!isEditable}
                        type="text"
                        value={editedValues.allowVlan}
                        onChange={(e) =>
                          updateEditedValues("allowVlan", e.target.value)
                        }
                        sx={getInputStyles("allowVlan")}
                        placeholder="e.g. 1, 4-7"
                      />
                      <Typography
                        sx={{
                          color: theme.palette.primary.greySecound,
                          fontSize: theme.typography.customFontSize[1],
                        }}
                      >
                        {/* (1~4094) */}
                        {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      </Typography>
                    </Box>

                  </Box>
                  {
                    vlanIdsCount.number > 128 &&
                    <>
                      <Box
                        sx={{
                          ...rowStyle,
                          marginTop: "0"
                        }}
                      >
                        <Typography
                          className={mainStyle["collapse-typography"]}
                          sx={{ alignSelf: "flex-start", pt: "6px", minWidth: "175px !important;" }}
                        >
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.primary.redPrimary,
                            fontSize: "0.75rem",
                          }}
                        >
                          Limit reached: Maximum 128 VLANs allowed.
                        </Typography>

                      </Box>
                    </>
                  }
                </>
              }

              {isPoeSectionVisible && (<>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  PoE State
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={profilePoeScheduleOn ? "schedule" : (selectedProfile?.poeState ? "enable" : "disable")}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const checked = value === "schedule" ? profilePoeScheduleOn : (value === "enable" ? (selectedProfile?.poeState && !profilePoeScheduleOn) : (!selectedProfile?.poeState && !profilePoeScheduleOn));

                      return (
                        <FormControlLabel
                          disabled
                          key={"profilePoeStateRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={<CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.isPoeScheduleMode ? "schedule" : (editedValues.poeState ? "enable" : "disable")}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateEditedValues("poeState", val === "enable" || val === "schedule");
                      updateEditedValues("isPoeScheduleMode", val === "schedule");
                    }}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const changed = isEdited.poeState != null || isEdited.isPoeScheduleMode != null;
                      const checked = value === "schedule" ? editedValues.isPoeScheduleMode : (value === "enable" ? (editedValues.poeState && !editedValues.isPoeScheduleMode) : (!editedValues.poeState && !editedValues.isPoeScheduleMode));

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={"poeStateRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              {isProfileMode && profilePoeScheduleOn && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}></Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <MenuSelect
                      disabled
                      value={selectedProfile?.poeSchedule || ""}
                      options={scheduleOptions}
                      width="300px"
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </Box>
              )}
              {!isProfileMode && editedValues.isPoeScheduleMode && (
                <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}></Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <MenuSelect
                      disabled={!isEditable}
                      value={editedValues.poeSchedule || ""}
                      onChange={(opt) => {
                        if (opt.value === "Create New Schedule") {
                          setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                          return;
                        }
                        updateEditedValues("poeSchedule", opt.value);
                      }}
                      options={scheduleOptionsWithLimit}
                      placeholder="Select a PoE Profile"
                      width="300px"
                      isEditedField={isEdited.poeSchedule}
                      pinningOptions={[
                        {
                          value: "Create New Schedule",
                          label: "Create New Schedule",
                          showArrow: true,
                        }
                      ]}
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </Box>
              )}
              </>)}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >

                {
                  isCopper &&
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                      Copper Link Speed
                    </Typography>

                    {
                      isProfileMode &&
                      <MenuSelect
                        disabled
                        value={selectedProfile?.speed || ""}
                        options={linkSpeedOptions}
                        width="300px"
                        isEditedField={isEdited.speed}
                      />
                    }

                    {
                      !isProfileMode && linkSpeedData.copper.length > 0 &&
                      <MenuSelect
                        disabled={!isEditable}
                        value={editedValues?.speed || ""}
                        onChange={(opt) => updateEditedValues("speed", opt.value)}
                        options={linkSpeedData.copper.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        width="300px"
                        isEditedField={isEdited.speed}
                      />
                    }
                  </Box>
                }

                {
                  isFiber &&
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                      Fiber Link Speed
                    </Typography>
                    {
                      isProfileMode &&
                      <MenuSelect
                        disabled
                        value={selectedProfile?.sfpSpeed || ""}
                        options={linkSpeedOptions}
                        width="300px"
                        isEditedField={isEdited.comboSfpSpeed}
                      />
                    }

                    {
                      !isProfileMode && linkSpeedData.fiber.length > 0 &&
                      <MenuSelect
                        disabled={!isEditable}
                        value={isCombo ? (editedValues?.comboSfpSpeed || editedValues?.speedS || editedValues?.speed || "") : (editedValues?.speed || editedValues?.speedS || editedValues?.comboSfpSpeed || "")}
                        onChange={(opt) => {
                          if (isCombo) {
                            updateEditedValues("comboSfpSpeed", opt.value);
                          } else {
                            updateEditedValues("speed", opt.value);
                            updateEditedValues("comboSfpSpeed", opt.value);
                          }
                        }}
                        options={linkSpeedData.fiber.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        width="300px"
                        isEditedField={isCombo ? isEdited.comboSfpSpeed : (isEdited.speed || isEdited.comboSfpSpeed)}
                      />
                    }
                  </Box>

                }
              </Box>

              {/* Operation */}
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  Operation
                </Typography>
                {isProfileMode && (
                  <MenuSelect
                    disabled
                    value={selectedProfile?.operation?.type || "switching"}
                    options={defaultOperationList}
                    width="300px"
                  />
                )}
                {!isProfileMode && (
                  <MenuSelect
                    disabled={!isEditable}
                    value={editedValues.operation?.type || "switching"}
                    onChange={(opt) => {
                      const newType = opt.value;
                      const macAddress = selectedRows[0]?.original?.macAddress;
                      const newGroupId = newType === "aggregation"
                        ? getAvailableAggregateGroupId(switchesData, macAddress, editedValues.operation?.aggregateGroupId, selectedRows[0]?.original?.aggregation)
                        : null;
                      const sourceIndices = selectedRows.map(r => r.original?.portIndex);
                      updateEditedValues("operation", {
                        type: newType,
                        mirrorPort: newType === "mirroring" ? (editedValues.operation?.mirrorPort || []) : [],
                        mirrorMode: newType === "mirroring" ? (editedValues.operation?.mirrorMode || "both") : "",
                        aggregatePort: newType === "aggregation"
                          ? (editedValues.operation?.aggregatePort || []).map(Number).filter(idx => !sourceIndices.includes(idx))
                          : [],
                        aggregateType: newType === "aggregation"
                          ? (["lacp", "static"].includes((editedValues.operation?.aggregateType || "").toLowerCase())
                            ? editedValues.operation.aggregateType.toLowerCase()
                            : "lacp")
                          : "",
                        aggregateGroupId: newGroupId
                      });
                    }}
                    options={modalOperationList}
                    width="300px"
                  />
                )}
              </Box>

              {/* Mirroring Port Selector Grid */}
              {!isProfileMode && editedValues.operation?.type === "mirroring" && (
                <Box sx={{ mt: 1.5, pl: "225px", width: "100%", boxSizing: "border-box", mb: 2.5 }}>
                  {/* Legend */}
                  <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                    <Box sx={legendItemStyle}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                      <Typography variant="caption">Unselected</Typography>
                    </Box>
                    <Box sx={legendItemStyle}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main, borderRadius: "2px" }} />
                      <Typography variant="caption">Source Port</Typography>
                    </Box>
                    <Box sx={legendItemStyle}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.blueSecound, borderRadius: "2px" }} />
                      <Typography variant="caption">Destination Port</Typography>
                    </Box>
                  </Box>
                  {/* Grid */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2 }}>
                    {devicePorts.map((p) => {
                      const portIdx = p.portIndex;
                      const isSourcePort = selectedRows.map(r => r.original?.portIndex).includes(portIdx);
                      let status = "unselected";
                      if (isSourcePort) {
                        status = "source";
                      } else if ((editedValues.operation?.mirrorPort || []).map(Number).includes(portIdx)) {
                        status = "destination";
                      }

                      return (
                        <Box
                          key={portIdx}
                          sx={portBoxStyle(status, theme)}
                          onClick={() => {
                            if (!isEditable) return;
                            if (isSourcePort) return;
                            const currentMirror = (editedValues.operation?.mirrorPort || []).map(Number);
                            let updatedMirror;
                            if (status === "destination") {
                              updatedMirror = currentMirror.filter(id => id !== portIdx);
                            } else {
                              updatedMirror = [...currentMirror, portIdx].sort((a, b) => a - b);
                            }
                            updateEditedValues("operation", {
                              ...editedValues.operation,
                              mirrorPort: updatedMirror
                            });
                          }}
                        >
                          {portIdx}
                        </Box>
                      );
                    })}
                  </Box>
                  {/* Mirror Mode Dropdown */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Mirror Mode</Typography>
                    <MenuSelect
                      disabled={!isEditable}
                      value={editedValues.operation?.mirrorMode || "both"}
                      onChange={(opt) => {
                        updateEditedValues("operation", {
                          ...editedValues.operation,
                          mirrorMode: opt.value
                        });
                      }}
                      options={[
                        { label: "Tx", value: "egress" },
                        { label: "Rx", value: "ingress" },
                        { label: "Both", value: "both" }
                      ]}
                      width="150px"
                    />
                  </Box>
                </Box>
              )}

              {/* Aggregation Port Selector Grid */}
              {!isProfileMode && editedValues.operation?.type === "aggregation" && (
                <Box sx={{ mt: 1.5, pl: "225px", width: "100%", boxSizing: "border-box", mb: 2.5 }}>
                  {/* Legend */}
                  <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                    <Box sx={legendItemStyle}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                      <Typography variant="caption">Unselected</Typography>
                    </Box>
                    <Box sx={{ ...legendItemStyle }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main || "#172664", borderRadius: "2px" }} />
                      <Typography variant="caption">Selected</Typography>
                    </Box>
                  </Box>
                  {/* Grid */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 0.5 }}>
                    {(() => {
                      const sourceIndices = selectedRows.map(r => r.original?.portIndex);
                      // aggregateGroupId 為 null 代表組數額度已滿, 這個 port 沒辦法建立新群組,
                      // 整個選取格都要鎖住 (跟「這個群組本身已滿員」是不同狀況).
                      const isNoGroupAvailable = editedValues.operation?.aggregateGroupId == null;
                      const maxAggregationPorts = selectedRows[0]?.original?.aggregation;
                      const currentGroupSize = sourceIndices.length + (editedValues.operation?.aggregatePort || []).length;
                      const isGroupFull = maxAggregationPorts != null && currentGroupSize >= maxAggregationPorts;

                      return devicePorts.map((p) => {
                      const portIdx = p.portIndex;
                      const isSourcePort = sourceIndices.includes(portIdx);
                      const isSelectedInGrid = (editedValues.operation?.aggregatePort || []).map(Number).includes(portIdx);
                      const isOtherAggregated = isPortInOtherAggregateGroup(p);
                      let status = "unselected";
                      if (isSourcePort || isSelectedInGrid) {
                        status = "selected";
                      } else if (isNoGroupAvailable) {
                        status = "noGroup";
                      } else if (isOtherAggregated) {
                        status = "disabled";
                      } else if (isGroupFull) {
                        status = "full";
                      }

                      const renderBox = (
                        <Box
                          key={portIdx}
                          sx={portBoxStyle(status === "full" || status === "noGroup" ? "disabled" : status, theme)}
                          onClick={() => {
                            if (!isEditable) return;
                            if (status === "disabled" || status === "full" || status === "noGroup" || isSourcePort) return;
                            const currentPorts = (editedValues.operation?.aggregatePort || []).map(Number).filter(id => !sourceIndices.includes(id));
                            let updatedPorts;
                            if (isSelectedInGrid) {
                              updatedPorts = currentPorts.filter((id) => id !== portIdx);
                            } else {
                              updatedPorts = [...currentPorts, portIdx].sort((a, b) => a - b);
                            }
                            updateEditedValues("operation", {
                              ...editedValues.operation,
                              aggregatePort: updatedPorts
                            });
                          }}
                        >
                          {portIdx}
                        </Box>
                      );

                      if (status === "disabled" || status === "full" || status === "noGroup") {
                        let title = "This port is part of a different aggregation group.";
                        if (status === "full") title = "Maximum ports reached.";
                        if (status === "noGroup") title = "Maximum groups reached.";
                        return (
                          <Tooltip
                            key={portIdx}
                            arrow
                            title={title}
                          >
                            <span style={{ display: "inline-block" }}>
                              {renderBox}
                            </span>
                          </Tooltip>
                        );
                      }
                      return renderBox;
                      });
                    })()}
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: "orange" }}>
                      All selected ports will be synchronized with these settings.
                    </Typography>
                  </Box>
                  {/* Aggregating Mode Dropdown */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                    <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Aggregating Mode</Typography>
                    <MenuSelect
                      disabled={!isEditable}
                      value={(editedValues.operation?.aggregateType || "lacp").toLowerCase()}
                      onChange={(opt) => {
                        updateEditedValues("operation", {
                          ...editedValues.operation,
                          aggregateType: opt.value.toLowerCase()
                        });
                      }}
                      options={[
                        { label: "LACP", value: "lacp" },
                        { label: "Static", value: "static" }
                      ]}
                      width="150px"
                    />
                  </Box>
                  {/* Group ID */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Group ID</Typography>
                    {editedValues.operation?.aggregateGroupId != null ? (
                      <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                        {editedValues.operation.aggregateGroupId}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 14, color: "error.main" }}>
                        Maximum groups reached.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  DDP
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.ddpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultStateTypes).map((key) => {
                      const ddpState = defaultStateTypes[key];
                      const changed = isEdited.ddpState != null;

                      return (
                        <FormControlLabel
                          disabled
                          key={ddpState.key}
                          value={ddpState.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                            />
                          }
                          label={ddpState.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.ddpState ? "enable" : "disable"}
                    onChange={(e) =>
                      updateEditedValues("ddpState", e.target.value === "enable")
                    }
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultStateTypes).map((key) => {
                      const ddpState = defaultStateTypes[key];
                      const changed = isEdited.ddpState != null;

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={ddpState.key}
                          value={ddpState.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                            />
                          }
                          label={ddpState.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              {/* LBD */}
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                  LBD
                </Typography>
                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.lbdState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const lbdState = defaultStateTypes[key];
                        const changed = isEdited.lbdState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={lbdState.key}
                            value={lbdState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              />
                            }
                            label={lbdState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.lbdState ? "enable" : "disable"}
                    onChange={(e) =>
                      updateEditedValues("lbdState", e.target.value === "enable")
                    }
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultStateTypes).map((key) => {
                      const lbdState = defaultStateTypes[key];
                      const changed = isEdited.lbdState != null;

                      return (
                        <FormControlLabel
                          disabled={!isEditable || editedValues.rstpState === true}
                          key={lbdState.key}
                          value={lbdState.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                            />
                          }
                          label={lbdState.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              {/* RSTP */}
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;", display: "flex", alignItems: "center" }}>
                  RSTP
                  <Tooltip title="When RSTP is enabled as the primary loop prevention mechanism, LBD should not be enabled." placement="top">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: 16,
                        ml: "5px",
                        "& img": {
                          content: `url(${icon_snmp})`,
                        },
                        "&:hover img": {
                          content: `url(${icon_snmp_hover})`,
                        },
                      }}
                    >
                      <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                    </Box>
                  </Tooltip>
                </Typography>
                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.rstpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const checked = value === "enable" ? selectedProfile?.rstpState : !selectedProfile?.rstpState;

                      return (
                        <FormControlLabel
                          disabled
                          key={"profileRstpRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={<CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={editedValues.rstpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const changed = isEdited.rstpState != null;
                      const checked = value === "enable" ? editedValues.rstpState : !editedValues.rstpState;

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={"rstpRadioButton" + idx}
                          value={value}
                          onClick={() => {
                            if (!isEditable) return;
                            const isEnable = value === "enable";
                            updateEditedValues("rstpState", isEnable);
                            if (isEnable) {
                              updateEditedValues("lbdState", false);
                            } else {
                              updateEditedValues("stpState", false);
                            }
                          }}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </Box>

              {/* STP Guard */}
              {((isProfileMode ? selectedProfile?.rstpState : editedValues?.rstpState) === true) && (
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                    STP Guard
                  </Typography>
                  {
                    isProfileMode &&
                    <RadioGroup
                      disabled
                      row
                      value={selectedProfile?.stpState ? "enable" : "disable"}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const checked = value === "enable" ? selectedProfile?.stpState : !selectedProfile?.stpState;

                        return (
                          <FormControlLabel
                            disabled
                            key={"profileStpRadioButton" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={<CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }
                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      value={editedValues.stpState ? "enable" : "disable"}
                      onChange={(e) =>
                        updateEditedValues("stpState", e.target.value === "enable")
                      }
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const changed = isEdited.stpState != null;
                        const checked = value === "enable" ? editedValues.stpState : !editedValues.stpState;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={"stpRadioButton" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }
                </Box>
              )}

              {/* Access Policy */}
              {!isAccessPolicyHidden && isProfileMode && selectedProfile?.portType === "access" && (
                <>
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "180px !important;" }}>
                      Access Policy
                      {isUnityProSubscribed || !selectedProfile?.accessPolicy?.enableState ? (
                        <ProColorIcon aria-label="Pro" style={proIconStyle} />
                      ) : (
                        <Tooltip
                          title="Pro feature disabled"
                          placement="bottom"
                          arrow
                        >
                          <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                        </Tooltip>
                      )}
                    </Typography>
                    <MenuSelect
                      disabled
                      value={
                        selectedProfile?.accessPolicy?.enableState
                          ? selectedProfile.accessPolicy.type
                          : accessPolicyTypeDefinition.DISABLED.value
                      }
                      options={defaultAccessPolicyTypeList}
                      width="300px"
                    />
                  </Box>

                  {selectedProfile?.accessPolicy?.enableState &&
                    (selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                      selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                      <MacWhitelistSection
                        macProfile={selectedProfile.accessPolicy.macList || null}
                        macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                        macProfilesRaw={macProfiles}
                        showEditGeneral={false}
                        showDynamicSize={selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                        dynamicSize={selectedProfile.accessPolicy.dynamicSize}
                        isEdited={isEdited}
                        layout="modal"
                      />
                    )}

                  {selectedProfile?.accessPolicy?.enableState &&
                    selectedProfile.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                      <RadiusProfileSection
                        radiusProfile={selectedProfile.accessPolicy.radiusProfile || null}
                        radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                        radiusProfilesRaw={radiusProfiles}
                        showEditGeneral={false}
                        layout="modal"
                      />
                    )}
                </>
              )}

              {!isAccessPolicyHidden && !isProfileMode && editedValues.portType === "access" && (
                <>
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: "175px !important;" }}>
                      Access Policy
                      {isUnityProSubscribed || !editedValues.accessPolicy?.enableState ? (
                        <ProColorIcon aria-label="Pro" style={proIconStyle} />
                      ) : (
                        <Tooltip
                          title="Pro feature disabled"
                          placement="bottom"
                          arrow
                        >
                          <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                        </Tooltip>
                      )}
                    </Typography>
                    <MenuSelect
                      disabled={!isEditable}
                      value={
                        editedValues.accessPolicy?.enableState
                          ? editedValues.accessPolicy.type
                          : accessPolicyTypeDefinition.DISABLED.value
                      }
                      onChange={(opt) => {
                        updateEditedValues("accessPolicy", {
                          ...editedValues.accessPolicy,
                          enableState: opt.value !== accessPolicyTypeDefinition.DISABLED.value,
                          type: opt.value === accessPolicyTypeDefinition.DISABLED.value
                            ? accessPolicyTypeDefinition.STATIC.value
                            : opt.value,
                        });
                      }}
                      options={defaultAccessPolicyTypeList}
                      width="300px"
                      isEditedField={isEdited.accessPolicy}
                    />
                  </Box>

                  {editedValues.accessPolicy?.enableState &&
                    (editedValues.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                      editedValues.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                      <MacWhitelistSection
                        macProfile={editedValues.accessPolicy.macList || null}
                        onChangeMacProfile={(opt) => {
                          if (opt.value === "Edit/Create New Profile") {
                            setLeaveModalState({ open: true, profileType: "Mac Address", page: "mac-address" });
                            return;
                          }
                          updateEditedValues("accessPolicy", {
                            ...editedValues.accessPolicy,
                            macList: opt.value,
                          });
                        }}
                        macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                        macProfilesRaw={macProfiles}
                        showEditGeneral={isEditable}
                        showDynamicSize={editedValues.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                        dynamicSize={editedValues.accessPolicy.dynamicSize}
                        onChangeDynamicSize={(val) => {
                          updateEditedValues("accessPolicy", {
                            ...editedValues.accessPolicy,
                            dynamicSize: val === "" ? null : parseInt(val, 10),
                          });
                        }}
                        layout="modal"
                      />
                    )}

                  {editedValues.accessPolicy?.enableState &&
                    editedValues.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                      <RadiusProfileSection
                        radiusProfile={editedValues.accessPolicy.radiusProfile || null}
                        onChangeRadiusProfile={(opt) => {
                          if (opt.value === "Edit/Create New Profile") {
                            setLeaveModalState({ open: true, profileType: "RADIUS Server", page: "radius-server" });
                            return;
                          }
                          updateEditedValues("accessPolicy", {
                            ...editedValues.accessPolicy,
                            radiusProfile: opt.value,
                          });
                        }}
                        radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                        radiusProfilesRaw={radiusProfiles}
                        showEditGeneral={isEditable}
                        layout="modal"
                      />
                    )}
                </>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
            onClick={handleCancel}
          >
            Revert
          </OutlineButton>
          <SolidButton
            width="100px"
            height="32px"
            disabled={isFormInvalid()}
            onClick={handleSaveClick}
          >
            Save
          </SolidButton>
        </Box>
      </ModalContainer>

      <AggregationConfirmModal
        showAggregationConfirmModal={showAggregationConfirmModal}
        setShowAggregationConfirmModal={setShowAggregationConfirmModal}
        onProceedCallback={() => onSave()}
      />
      <LeaveConfirmModal
        isOpen={leaveModalState.open}
        onClose={() => setLeaveModalState(prev => ({ ...prev, open: false }))}
        leaveCallback={handleLeaveConfirm}
        profileType={leaveModalState.profileType}
      />
      <LinkModal
        open={isLinkModalOpen}
        transparentBackdrop
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </>
  );
};

export default SwitchEditModal;
